import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Mail, MessageSquare, Send, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Seo } from '@/components/Seo';
import { Reveal } from '@/components/Reveal';
import { CONTACT } from '@/constants/testIds';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputClass = 'w-full rounded-xl border border-white/10 bg-elevated px-4 py-3 text-sm text-white placeholder:text-white/40 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand transition-colors';

export default function Contact() {
  const { t } = useTranslation();
  const topics = t('contact.topics', { returnObjects: true });
  const [form, setForm] = useState({ name: '', email: '', topic: 'general', message: '' });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setDone(true);
      toast.success(t('contact.toastSuccess'));
      setForm({ name: '', email: '', topic: 'general', message: '' });
    } catch {
      toast.error(t('contact.toastError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Seo title={t('contact.seoTitle')} description={t('contact.seoDescription')} />

      <section className="relative overflow-hidden px-6 pb-24 pt-32 lg:px-8 sm:pt-40">
        <div className="pointer-events-none absolute left-1/2 top-24 -z-10 h-96 w-96 -translate-x-1/2 rounded-full bg-brand/10 blur-[120px]" />
        <div className="mx-auto grid max-w-6xl gap-12 lg:grid-cols-[1fr_1.3fr]">
          <div className="text-center lg:text-left">
            <span className="eyebrow">{t('contact.eyebrow')}</span>
            <h1 className="mt-4 font-display text-4xl font-extrabold leading-tight text-white sm:text-5xl">{t('contact.title')}</h1>
            <p className="mt-4 text-white/60">{t('contact.subtitle')}</p>
            <div className="mx-auto mt-8 w-fit space-y-4 lg:mx-0">
              <a href="mailto:support@copy.click" className="flex items-center gap-3 text-white/70 hover:text-brand">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand/10 text-brand"><Mail className="h-5 w-5" /></span>
                support@copy.click
              </a>
              <div className="flex items-center gap-3 text-white/70">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand/10 text-brand"><MessageSquare className="h-5 w-5" /></span>
                {t('contact.liveChat')}
              </div>
            </div>
          </div>

          <Reveal delay={0.1}>
            <form onSubmit={submit} data-testid={CONTACT.form} className="rounded-2xl border border-white/10 bg-surface/70 p-8 backdrop-blur-xl">
              {done ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="flex h-14 w-14 items-center justify-center rounded-full bg-success/15 text-success"><Send className="h-6 w-6" /></div>
                  <h3 className="mt-4 font-display text-xl font-semibold text-white">{t('contact.sentTitle')}</h3>
                  <p className="mt-2 text-sm text-white/60">{t('contact.sentSubtitle')}</p>
                  <button type="button" onClick={() => setDone(false)} className="btn-secondary mt-6 text-sm">{t('contact.sendAnother')}</button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <label className="mb-2 block text-sm text-white/70">{t('contact.labelName')}</label>
                      <input required value={form.name} onChange={update('name')} data-testid={CONTACT.name} className={inputClass} placeholder={t('contact.placeholderName')} />
                    </div>
                    <div>
                      <label className="mb-2 block text-sm text-white/70">{t('contact.labelEmail')}</label>
                      <input required type="email" value={form.email} onChange={update('email')} data-testid={CONTACT.email} className={inputClass} placeholder={t('contact.placeholderEmail')} />
                    </div>
                  </div>
                  <div>
                    <label className="mb-2 block text-sm text-white/70">{t('contact.labelTopic')}</label>
                    <select value={form.topic} onChange={update('topic')} data-testid={CONTACT.topic} className={inputClass}>
                      {topics.map((tp) => <option key={tp.value} value={tp.value} className="bg-elevated">{tp.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="mb-2 block text-sm text-white/70">{t('contact.labelMessage')}</label>
                    <textarea required rows={5} value={form.message} onChange={update('message')} data-testid={CONTACT.message} className={inputClass} placeholder={t('contact.placeholderMessage')} />
                  </div>
                  <button type="submit" disabled={loading} data-testid={CONTACT.submit} className="btn-primary w-full justify-center">
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>{t('contact.send')} <Send className="h-4 w-4" /></>}
                  </button>
                </div>
              )}
            </form>
          </Reveal>
        </div>
      </section>
    </>
  );
}
